import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit2, Trash2, Image, Save, X, Camera, ImagePlus, ChevronLeft, ChevronRight, MapPin, RotateCw, RotateCcw, Check, Sparkles, Loader2, Crop, Undo2 } from 'lucide-react';
import { api } from '../services/api';
import { ITEM_CATEGORIES, ITEM_CONDITIONS, ITEM_CONDITION_LABELS, type ItemCategory, type ItemCondition, type UpdateItemInput } from '@findmystuff/shared';
import clsx from 'clsx';
import { ImageLightbox } from '../components/common/ImageLightbox';
import { PaywallModal } from '../components/common/PaywallModal';
import { PhraseDropdown } from '../components/common/PhraseDropdown';
import { GpsMap } from '../components/common/GpsMap';
import { useAiCredits } from '../hooks/useAiCredits';

interface LocationImageData {
  data: string;
  mimeType: string;
  preview: string;
}

export function ItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(true);
  const [editData, setEditData] = useState<UpdateItemInput>({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [locationImageData, setLocationImageData] = useState<LocationImageData | null>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  // Saved locations state
  const [locationMode, setLocationMode] = useState<'select' | 'new'>('select');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // AI Credits
  const { hasCredits, consumeCredit } = useAiCredits();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');

  // Fetch locations from API
  const { data: savedLocations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.locations.list,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const locationImageInputRef = useRef<HTMLInputElement>(null);

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', id],
    queryFn: () => api.items.get(id!),
    enabled: !!id,
  });

  const { data: inventories = [] } = useQuery({
    queryKey: ['inventories'],
    queryFn: () => api.inventories.list(),
  });

  // Initialize edit data when item loads (since editing is on by default)
  useEffect(() => {
    if (item && savedLocations) {
      setEditData({
        title: item.title,
        description: item.description,
        location: item.location || '',
        category: item.category as ItemCategory,
        condition: (item.condition as ItemCondition) || 'good',
      });
      setLocationImageData(null);

      // Check if item's location matches a saved location
      const matchingLocation = savedLocations.find(
        loc => loc.name.toLowerCase() === (item.location || '').toLowerCase()
      );

      if (matchingLocation) {
        setSelectedLocationId(matchingLocation.id);
        setLocationMode('select');
      } else if (item.location) {
        // Item has a custom location not in saved locations
        setSelectedLocationId(null);
        setLocationMode('new');
      } else {
        // Item has no location - keep it empty, don't auto-select
        setSelectedLocationId(null);
        setLocationMode('select');
      }
    }
  }, [item, savedLocations]);

  const [transferInventoryId, setTransferInventoryId] = useState<string>('');
  const [transferSuccessModal, setTransferSuccessModal] = useState<{ show: boolean; inventoryName: string }>({ show: false, inventoryName: '' });

  const transferMutation = useMutation({
    mutationFn: (inventoryId: string) => api.items.transfer(id!, inventoryId),
    onSuccess: (_data, inventoryId) => {
      const targetInventory = inventories.find(inv => inv.id === inventoryId);
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      setTransferInventoryId('');
      setTransferSuccessModal({ show: true, inventoryName: targetInventory?.name || 'the selected inventory' });
    },
  });

  const handleTransferModalClose = () => {
    setTransferSuccessModal({ show: false, inventoryName: '' });
    navigate(-1);
  };

  const updateMutation = useMutation({
    mutationFn: (data: UpdateItemInput) => api.items.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      setIsEditing(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.items.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      navigate(-1);
    },
  });

  const generateImageMutation = useMutation({
    mutationFn: () => api.items.generateImage(id!),
  });

  const generateContentMutation = useMutation({
    mutationFn: () => api.items.generateContent(id!),
    onSuccess: (data) => {
      // Update the edit data with AI-generated content
      setEditData((prev) => ({
        ...prev,
        title: data.title,
        description: data.description,
      }));
    },
  });

  const addImagesMutation = useMutation({
    mutationFn: (images: Array<{ data: string; mimeType: string }>) =>
      api.items.addImages(id!, images),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      setIsUploadingImages(false);
    },
    onError: () => {
      setIsUploadingImages(false);
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: string) => api.items.deleteOneImage(id!, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      // Reset selected index if needed
      if (item?.images && selectedImageIndex >= item.images.length - 1) {
        setSelectedImageIndex(Math.max(0, selectedImageIndex - 1));
      }
    },
  });

  const reorderImagesMutation = useMutation({
    mutationFn: (imageIds: string[]) => api.items.reorderImages(id!, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] }); // Refresh list thumbnails too
    },
  });

  const createLocationMutation = useMutation({
    mutationFn: ({ name, imageData }: { name: string; imageData?: { data: string; mimeType: string } }) =>
      api.locations.create(name, imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });

  // AI generation handlers with credit checking
  const handleGenerateImage = async () => {
    if (!hasCredits) {
      setPaywallFeature('AI Image Generation');
      setShowPaywall(true);
      return;
    }

    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('AI Image Generation');
      setShowPaywall(true);
      return;
    }

    generateImageMutation.mutate();
  };

  const handleGenerateContent = async () => {
    if (!hasCredits) {
      setPaywallFeature('AI Content Generation');
      setShowPaywall(true);
      return;
    }

    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('AI Content Generation');
      setShowPaywall(true);
      return;
    }

    generateContentMutation.mutate();
  };

  const handleAiCrop = async (imageId: string) => {
    if (!hasCredits) {
      setPaywallFeature('AI Crop');
      setShowPaywall(true);
      return;
    }

    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('AI Crop');
      setShowPaywall(true);
      return;
    }

    aiCropImageMutation.mutate(imageId);
  };

  const rotateImageMutation = useMutation({
    mutationFn: ({ imageId, direction }: { imageId: string; direction: 'left' | 'right' }) =>
      api.items.rotateImage(id!, imageId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const aiCropImageMutation = useMutation({
    mutationFn: (imageId: string) => api.items.aiCropImage(id!, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const undoAiCropMutation = useMutation({
    mutationFn: (imageId: string) => api.items.undoAiCrop(id!, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', id] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingImages(true);
    const images: Array<{ data: string; mimeType: string }> = [];

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.readAsDataURL(file);
      });

      if (base64) {
        images.push({ data: base64, mimeType: file.type });
      }
    }

    if (images.length > 0) {
      addImagesMutation.mutate(images);
    } else {
      setIsUploadingImages(false);
    }

    e.target.value = '';
  };

  const handleDeleteImage = (imageId: string) => {
    if (confirm('Delete this photo?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!item?.images) return;
    const newIndex = direction === 'prev'
      ? (selectedImageIndex - 1 + item.images.length) % item.images.length
      : (selectedImageIndex + 1) % item.images.length;
    setSelectedImageIndex(newIndex);
  };

  // Drag and drop handlers (desktop)
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    const img = e.currentTarget.querySelector('img');
    if (img) {
      e.dataTransfer.setDragImage(img, 32, 32);
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    completeReorder(dropIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Touch handlers (mobile)
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    const touch = e.touches[0];
    if (touch) {
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      setDraggedIndex(index);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (draggedIndex === null || !item?.images) return;
    const touch = e.touches[0];
    if (!touch) return;

    // Find which thumbnail we're over
    for (let i = 0; i < thumbnailRefs.current.length; i++) {
      const ref = thumbnailRefs.current[i];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        if (
          touch.clientX >= rect.left &&
          touch.clientX <= rect.right &&
          touch.clientY >= rect.top &&
          touch.clientY <= rect.bottom
        ) {
          if (i !== draggedIndex) {
            setDragOverIndex(i);
          }
          break;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    if (dragOverIndex !== null) {
      completeReorder(dragOverIndex);
    } else {
      setDraggedIndex(null);
    }
    touchStartPos.current = null;
  };

  // Shared reorder logic
  const completeReorder = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex || !item?.images) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...item.images];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    if (draggedItem) {
      newOrder.splice(dropIndex, 0, draggedItem);
    }

    if (selectedImageIndex === draggedIndex) {
      setSelectedImageIndex(dropIndex);
    } else if (draggedIndex < selectedImageIndex && dropIndex >= selectedImageIndex) {
      setSelectedImageIndex(selectedImageIndex - 1);
    } else if (draggedIndex > selectedImageIndex && dropIndex <= selectedImageIndex) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }

    reorderImagesMutation.mutate(newOrder.map(img => img.id));

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Move image with arrow buttons (alternative for mobile)
  const moveImage = (index: number, direction: 'left' | 'right') => {
    if (!item?.images) return;
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= item.images.length) return;

    const newOrder = [...item.images];
    const [movedItem] = newOrder.splice(index, 1);
    if (movedItem) {
      newOrder.splice(newIndex, 0, movedItem);
    }

    if (selectedImageIndex === index) {
      setSelectedImageIndex(newIndex);
    } else if (index < selectedImageIndex && newIndex >= selectedImageIndex) {
      setSelectedImageIndex(selectedImageIndex - 1);
    } else if (index > selectedImageIndex && newIndex <= selectedImageIndex) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }

    reorderImagesMutation.mutate(newOrder.map(img => img.id));
  };

  const startEditing = () => {
    setEditData({
      title: item?.title,
      description: item?.description,
      location: item?.location || '',
      category: item?.category as ItemCategory,
      condition: (item?.condition as ItemCondition) || 'good',
    });
    setLocationImageData(null);

    // Check if item's location matches a saved location from API
    const matchingLocation = savedLocations.find(
      loc => loc.name.toLowerCase() === (item?.location || '').toLowerCase()
    );

    if (matchingLocation) {
      // Location exists in saved locations
      setSelectedLocationId(matchingLocation.id);
      setLocationMode('select');
    } else if (item?.location) {
      // Item has a location but it's not in saved list - use "new" mode with the location pre-filled
      setSelectedLocationId(null);
      setLocationMode('new');
    } else {
      // No location - default to most recently used or new
      const lastLocation = savedLocations[0]; // sorted by updatedAt desc
      if (lastLocation) {
        setSelectedLocationId(lastLocation.id);
        setEditData(prev => ({ ...prev, location: lastLocation.name }));
        setLocationMode('select');
      } else {
        setSelectedLocationId(null);
        setLocationMode('new');
      }
    }

    setIsEditing(true);
  };

  const handleLocationImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      if (base64) {
        setLocationImageData({
          data: base64,
          mimeType: file.type,
          preview: result,
        });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSelectSavedLocation = (locationId: string) => {
    const location = savedLocations.find(loc => loc.id === locationId);
    if (location) {
      setSelectedLocationId(locationId);
      setEditData(prev => ({ ...prev, location: location.name }));
      setLocationImageData(null);
      setLocationMode('select');
    }
  };

  const handleCreateNewLocation = () => {
    setSelectedLocationId(null);
    setEditData(prev => ({ ...prev, location: '' }));
    setLocationImageData(null);
    setLocationMode('new');
  };

  const saveChanges = async () => {
    const updatePayload: any = { ...editData };
    if (locationImageData) {
      updatePayload.locationImageData = {
        data: locationImageData.data,
        mimeType: locationImageData.mimeType,
      };
    }

    // If we have a new location with a name, save it to the API
    if (locationMode === 'new' && editData.location && editData.location.trim()) {
      try {
        await createLocationMutation.mutateAsync({
          name: editData.location.trim(),
          imageData: locationImageData ? {
            data: locationImageData.data,
            mimeType: locationImageData.mimeType,
          } : undefined,
        });
      } catch (error) {
        // Location might already exist, that's ok - upsert handles it
        console.log('Location save:', error);
      }
    }

    updateMutation.mutate(updatePayload);
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this item?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="card p-8 text-center text-gray-500">Loading item...</div>
    );
  }

  if (!item) {
    return (
      <div className="card p-8 text-center text-gray-500">Item not found</div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 overflow-x-hidden" style={{ overflowAnchor: 'none' }}>
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      {/* Main card */}
      <div className="card">
        {/* Image Gallery */}
        <div className="relative">
          {/* Main Image */}
          <div className="aspect-video bg-gray-100 rounded-t-xl flex items-center justify-center relative">
            {item.images && item.images.length > 0 ? (
              <>
                <img
                  src={item.images[selectedImageIndex]?.url}
                  alt={`${item.title} - Photo ${selectedImageIndex + 1}`}
                  className="w-full h-full object-contain cursor-pointer"
                  onClick={() => setShowImageLightbox(true)}
                />
                {/* Navigation arrows */}
                {item.images.length > 1 && (
                  <>
                    <button
                      onClick={() => navigateImage('prev')}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => navigateImage('next')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
                {/* Image counter */}
                {item.images.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white text-sm px-2 py-1 rounded">
                    {selectedImageIndex + 1} / {item.images.length}
                  </div>
                )}
                {/* Rotate left button */}
                <div className="absolute top-2 left-2">
                  <button
                    onClick={() => item.images[selectedImageIndex]?.id && rotateImageMutation.mutate({ imageId: item.images[selectedImageIndex].id, direction: 'left' })}
                    disabled={rotateImageMutation.isPending}
                    className="w-11 h-11 bg-black/50 hover:bg-black/70 active:bg-black/80 text-white rounded-full flex items-center justify-center touch-manipulation"
                    title="Rotate left 90°"
                  >
                    <RotateCcw className={clsx("w-5 h-5", rotateImageMutation.isPending && "animate-spin")} />
                  </button>
                </div>
                {/* Image action buttons */}
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => item.images[selectedImageIndex]?.id && rotateImageMutation.mutate({ imageId: item.images[selectedImageIndex].id, direction: 'right' })}
                    disabled={rotateImageMutation.isPending}
                    className="w-11 h-11 bg-black/50 hover:bg-black/70 active:bg-black/80 text-white rounded-full flex items-center justify-center touch-manipulation"
                    title="Rotate right 90°"
                  >
                    <RotateCw className={clsx("w-5 h-5", rotateImageMutation.isPending && "animate-spin")} />
                  </button>
                  {/* AI Crop / Undo Crop button */}
                  {item.images[selectedImageIndex]?.isAiCropped ? (
                    <button
                      onClick={() => item.images[selectedImageIndex]?.id && undoAiCropMutation.mutate(item.images[selectedImageIndex].id)}
                      disabled={undoAiCropMutation.isPending}
                      className="h-11 px-3 bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white rounded-full flex items-center justify-center gap-1.5 touch-manipulation text-sm font-medium"
                      title="Undo AI crop"
                    >
                      {undoAiCropMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Undo2 className="w-4 h-4" />
                      )}
                      Undo Crop
                    </button>
                  ) : (
                    <button
                      onClick={() => item.images[selectedImageIndex]?.id && handleAiCrop(item.images[selectedImageIndex].id)}
                      disabled={aiCropImageMutation.isPending}
                      className="h-11 px-3 bg-purple-500 hover:bg-purple-600 active:bg-purple-700 text-white rounded-full flex items-center justify-center gap-1.5 touch-manipulation text-sm font-medium"
                      title="AI crop to focus on main subject"
                    >
                      {aiCropImageMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Crop className="w-4 h-4" />
                      )}
                      AI Crop
                    </button>
                  )}
                  <button
                    onClick={() => item.images[selectedImageIndex]?.id && handleDeleteImage(item.images[selectedImageIndex].id)}
                    disabled={deleteImageMutation.isPending}
                    className="w-11 h-11 bg-red-500 hover:bg-red-600 active:bg-red-700 text-white rounded-full flex items-center justify-center touch-manipulation"
                    title="Delete this photo"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <span className="text-6xl">📦</span>
                <p className="mt-2 text-gray-500">No photos</p>
                <button
                  onClick={handleGenerateImage}
                  disabled={generateImageMutation.isPending}
                  className="mt-3 btn-secondary text-sm"
                >
                  <Image className="w-4 h-4 mr-1" />
                  {generateImageMutation.isPending ? 'Generating...' : 'Generate AI Image'}
                </button>
              </div>
            )}
          </div>

          {/* Thumbnails with drag-and-drop reorder */}
          {item.images && item.images.length > 1 && (
            <div className="bg-gray-50 p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  {item.images.length} photos — tap arrows or drag to reorder
                </span>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1">
                {item.images.map((img, index) => (
                  <div
                    key={img.id}
                    ref={(el) => { thumbnailRefs.current[index] = el; }}
                    className={clsx(
                      'flex-shrink-0 relative transition-transform',
                      draggedIndex === index && 'opacity-50 scale-95',
                      dragOverIndex === index && draggedIndex !== index && 'scale-105'
                    )}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={handleDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, index)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {/* Thumbnail badge for first image */}
                    {index === 0 && (
                      <span className="absolute -top-1 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded z-10 pointer-events-none">
                        Thumbnail
                      </span>
                    )}

                    {/* Drop indicator */}
                    {dragOverIndex === index && draggedIndex !== null && draggedIndex !== index && (
                      <div className="absolute -left-1.5 top-0 bottom-0 w-1 bg-primary-500 rounded-full" />
                    )}

                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => setSelectedImageIndex(index)}
                        className={clsx(
                          'w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors',
                          selectedImageIndex === index
                            ? 'border-primary-500'
                            : 'border-transparent hover:border-gray-300'
                        )}
                      >
                        <img
                          src={img.thumbnailUrl || img.url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover pointer-events-none"
                          draggable={false}
                        />
                      </button>
                      {/* Arrow buttons for reordering */}
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveImage(index, 'left')}
                          disabled={index === 0}
                          className={clsx(
                            'w-6 h-6 flex items-center justify-center rounded text-xs',
                            index === 0
                              ? 'text-gray-300'
                              : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'
                          )}
                        >
                          ←
                        </button>
                        <button
                          onClick={() => moveImage(index, 'right')}
                          disabled={index === item.images.length - 1}
                          className={clsx(
                            'w-6 h-6 flex items-center justify-center rounded text-xs',
                            index === item.images.length - 1
                              ? 'text-gray-300'
                              : 'text-gray-500 hover:bg-gray-200 active:bg-gray-300'
                          )}
                        >
                          →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add photos buttons - using labels for mobile compatibility */}
          <div className="flex gap-2 p-3 border-t">
            <input
              id="item-camera-input"
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="sr-only"
              disabled={isUploadingImages}
            />
            <input
              id="item-file-input"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              className="sr-only"
              disabled={isUploadingImages}
            />
            <label
              htmlFor="item-camera-input"
              className={clsx(
                "btn-secondary flex-1 text-sm flex items-center justify-center cursor-pointer",
                isUploadingImages && "opacity-50 pointer-events-none"
              )}
            >
              <Camera className="w-4 h-4 mr-1" />
              Take Photo
            </label>
            <label
              htmlFor="item-file-input"
              className={clsx(
                "btn-secondary flex-1 text-sm flex items-center justify-center cursor-pointer",
                isUploadingImages && "opacity-50 pointer-events-none"
              )}
            >
              <ImagePlus className="w-4 h-4 mr-1" />
              {isUploadingImages ? 'Uploading...' : 'Add Photos'}
            </label>
          </div>

          {/* GPS Map - show if selected image has coordinates */}
          {item.images && item.images[selectedImageIndex]?.latitude && item.images[selectedImageIndex]?.longitude && (
            <div className="p-3 border-t">
              <GpsMap
                latitude={item.images[selectedImageIndex].latitude!}
                longitude={item.images[selectedImageIndex].longitude!}
                title={item.title}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editData.title || ''}
                  onChange={(e) =>
                    setEditData({ ...editData, title: e.target.value })
                  }
                  className="input"
                  spellCheck={true}
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  autoComplete="one-time-code"
                />
              </div>

              {/* AI Content Generation Button */}
              {item?.images && item.images.length > 0 && (
                <button
                  type="button"
                  onClick={handleGenerateContent}
                  disabled={generateContentMutation.isPending}
                  className={clsx(
                    'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 border-dashed transition-colors',
                    generateContentMutation.isPending
                      ? 'bg-purple-50 border-purple-300 text-purple-600 cursor-wait'
                      : 'bg-white border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-400'
                  )}
                >
                  {generateContentMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing images...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate with AI
                    </>
                  )}
                </button>
              )}

              {generateContentMutation.isError && (
                <p className="text-sm text-red-600">
                  {generateContentMutation.error instanceof Error
                    ? generateContentMutation.error.message
                    : 'Failed to generate content'}
                </p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={editData.category || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      category: e.target.value as ItemCategory,
                    })
                  }
                  className="input"
                >
                  {ITEM_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition
                </label>
                <select
                  value={editData.condition || 'good'}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      condition: e.target.value as ItemCondition,
                    })
                  }
                  className="input"
                >
                  {ITEM_CONDITIONS.map((cond) => (
                    <option key={cond} value={cond}>
                      {ITEM_CONDITION_LABELS[cond]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <PhraseDropdown
                    onInsert={(phrase) => {
                      const currentDesc = editData.description || '';
                      const separator = currentDesc && !currentDesc.endsWith('\n') && !currentDesc.endsWith(' ') ? '\n' : '';
                      setEditData({ ...editData, description: currentDesc + separator + phrase });
                    }}
                  />
                </div>
                <textarea
                  value={editData.description || ''}
                  onChange={(e) =>
                    setEditData({ ...editData, description: e.target.value })
                  }
                  rows={4}
                  className="input"
                  spellCheck={true}
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  autoComplete="one-time-code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>

                {/* Location dropdown */}
                <select
                  value={locationMode === 'new' ? '__new__' : (selectedLocationId || '')}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '__new__') {
                      handleCreateNewLocation();
                    } else if (value === '') {
                      // No location selected
                      setSelectedLocationId(null);
                      setEditData(prev => ({ ...prev, location: '' }));
                      setLocationImageData(null);
                      setLocationMode('select');
                    } else {
                      handleSelectSavedLocation(value);
                    }
                  }}
                  className="input"
                >
                  <option value="">No location</option>
                  {savedLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                  <option value="__new__">+ Add new location...</option>
                </select>

                {/* Show selected location preview with image */}
                {locationMode === 'select' && selectedLocationId && (() => {
                  const selectedLocation = savedLocations.find(loc => loc.id === selectedLocationId);
                  const locationImage = selectedLocation?.imageUrl || item?.locationImage;
                  return (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {locationImage ? (
                          <img
                            src={locationImage}
                            alt="Location"
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                            <MapPin className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-gray-900">{editData.location}</p>
                          <p className="text-xs text-gray-500">Saved location</p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* New location input - show when adding new location */}
                {locationMode === 'new' && (
                  <div className="mt-3 space-y-3">
                    <input
                      type="text"
                      value={editData.location || ''}
                      onChange={(e) =>
                        setEditData({ ...editData, location: e.target.value })
                      }
                      placeholder="e.g. Top drawer of pantry, Under the bed"
                      className="input"
                      spellCheck={true}
                      autoCorrect="on"
                      autoCapitalize="sentences"
                  autoComplete="one-time-code"
                    />

                    {/* Location image upload */}
                    <input
                      id="edit-location-image-input"
                      ref={locationImageInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleLocationImageSelect}
                      className="sr-only"
                    />
                    {locationImageData ? (
                      <div className="flex items-center gap-3">
                        <div className="relative w-16 h-16 flex-shrink-0">
                          <img
                            src={locationImageData.preview}
                            alt="Location"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          <button
                            onClick={() => setLocationImageData(null)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-sm text-gray-600">New location photo</span>
                      </div>
                    ) : item?.locationImage ? (
                      <div className="flex items-center gap-3">
                        <img
                          src={item.locationImage}
                          alt="Location"
                          className="w-16 h-16 object-cover rounded-lg cursor-pointer"
                          onClick={() => setShowLocationModal(true)}
                        />
                        <div className="flex flex-col gap-1">
                          <span className="text-sm text-gray-600">Current location photo</span>
                          <label
                            htmlFor="edit-location-image-input"
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                          >
                            <Camera className="w-4 h-4" />
                            Change photo
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label
                        htmlFor="edit-location-image-input"
                        className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                      >
                        <Camera className="w-4 h-4" />
                        Add photo of location
                      </label>
                    )}
                  </div>
                )}
              </div>

              {/* Transfer to another inventory */}
              {inventories.length > 1 && (
                <div className="pt-4 border-t">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transfer to another inventory
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={transferInventoryId}
                      onChange={(e) => setTransferInventoryId(e.target.value)}
                      className="input flex-1"
                    >
                      <option value="">Select inventory...</option>
                      {inventories
                        .filter((inv) => inv.id !== item?.inventoryId)
                        .map((inv) => (
                          <option key={inv.id} value={inv.id}>
                            {inv.name}
                          </option>
                        ))}
                    </select>
                    <button
                      onClick={() => transferInventoryId && transferMutation.mutate(transferInventoryId)}
                      disabled={!transferInventoryId || transferMutation.isPending}
                      className="btn-secondary"
                    >
                      {transferMutation.isPending ? 'Transferring...' : 'Transfer'}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4">
                <button
                  onClick={saveChanges}
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="btn-danger ml-auto"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {item.title}
                  </h1>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-sm bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {item.category}
                    </span>
                    {item.subCategory && (
                      <span className="text-sm text-gray-500">
                        / {item.subCategory}
                      </span>
                    )}
                    {item.condition && (
                      <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {ITEM_CONDITION_LABELS[item.condition as ItemCondition] || item.condition}
                      </span>
                    )}
                    {item.needsReview && (
                      <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                        Needs Review
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={startEditing} className="btn-secondary">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDelete}
                    className="btn-danger"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {item.description && (
                <p className="mt-4 text-gray-600">{item.description}</p>
              )}

              {(item.location || item.locationImage) && (
                <div className="mt-4">
                  <div className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="w-4 h-4" />
                    Location
                  </div>
                  <div className="flex items-start gap-3">
                    {item.locationImage && (
                      <button
                        onClick={() => setShowLocationModal(true)}
                        className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-primary-400 transition-colors"
                      >
                        <img
                          src={item.locationImage}
                          alt="Location"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )}
                    {item.location && (
                      <span className="text-gray-600 pt-1">{item.location}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Attributes */}
              {item.attributes && Object.keys(item.attributes).length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Details
                  </h3>
                  <dl className="grid grid-cols-2 gap-2">
                    {Object.entries(item.attributes)
                      .filter(([_, value]) => value)
                      .map(([key, value]) => (
                        <div key={key} className="bg-gray-50 p-2 rounded">
                          <dt className="text-xs text-gray-500 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </dt>
                          <dd className="text-sm text-gray-900">
                            {typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </div>
              )}

              {/* Discogs info */}
              {item.discogsReleaseId && (
                <div className="mt-6 p-3 bg-gray-900 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.6c-5.292 0-9.6-4.308-9.6-9.6S6.708 2.4 12 2.4s9.6 4.308 9.6 9.6-4.308 9.6-9.6 9.6zm0-16.8c-3.972 0-7.2 3.228-7.2 7.2s3.228 7.2 7.2 7.2 7.2-3.228 7.2-7.2-3.228-7.2-7.2-7.2zm0 12c-2.652 0-4.8-2.148-4.8-4.8S9.348 7.2 12 7.2s4.8 2.148 4.8 4.8-2.148 4.8-4.8 4.8z"/>
                    </svg>
                    <span className="text-sm font-medium text-white">Discogs</span>
                  </div>
                  <dl className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <dt className="text-gray-400 text-xs">Release ID</dt>
                      <dd className="text-white font-mono">
                        <a
                          href={`https://www.discogs.com/release/${item.discogsReleaseId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-300 underline"
                        >
                          {item.discogsReleaseId}
                        </a>
                      </dd>
                    </div>
                    {item.discogsLabel && (
                      <div>
                        <dt className="text-gray-400 text-xs">Label</dt>
                        <dd className="text-white">{item.discogsLabel}</dd>
                      </div>
                    )}
                    {item.discogsCatno && (
                      <div>
                        <dt className="text-gray-400 text-xs">Catalog #</dt>
                        <dd className="text-white font-mono">{item.discogsCatno}</dd>
                      </div>
                    )}
                    {item.discogsPrice && (
                      <div>
                        <dt className="text-gray-400 text-xs">Price</dt>
                        <dd className="text-emerald-400 font-medium">${item.discogsPrice.toFixed(2)}</dd>
                      </div>
                    )}
                    {item.discogsMediaCondition && (
                      <div>
                        <dt className="text-gray-400 text-xs">Media</dt>
                        <dd className="text-white">{item.discogsMediaCondition}</dd>
                      </div>
                    )}
                    {item.discogsSleeveCondition && (
                      <div>
                        <dt className="text-gray-400 text-xs">Sleeve</dt>
                        <dd className="text-white">{item.discogsSleeveCondition}</dd>
                      </div>
                    )}
                  </dl>
                  {item.discogsComments && (
                    <p className="mt-2 text-sm text-gray-300 italic">"{item.discogsComments}"</p>
                  )}
                </div>
              )}

              {/* Source info */}
              {item.sourceType === 'voice' && (
                <div className="mt-6 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    Added via voice on{' '}
                    {new Date(item.createdAt).toLocaleDateString()}
                  </p>
                  {item.sourceTranscript && (
                    <p className="mt-1 text-sm text-blue-600 italic">
                      "{item.sourceTranscript}"
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Location Image Modal */}
      {showLocationModal && item.locationImage && (
        <ImageLightbox
          images={[{ url: item.locationImage }]}
          onClose={() => setShowLocationModal(false)}
          title={item.location || 'Location'}
        />
      )}

      {/* Transfer Success Modal */}
      {transferSuccessModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 mx-4 max-w-sm w-full shadow-xl">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Item Transferred
              </h3>
              <p className="text-gray-600 mb-6">
                This item has been moved to <span className="font-medium">{transferSuccessModal.inventoryName}</span>
              </p>
              <button
                onClick={handleTransferModalClose}
                className="btn-primary w-full"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox Modal */}
      {showImageLightbox && item.images && item.images.length > 0 && (
        <ImageLightbox
          images={item.images}
          initialIndex={selectedImageIndex}
          onClose={() => setShowImageLightbox(false)}
          title={item.title}
        />
      )}

      {/* AI Credits Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureName={paywallFeature}
      />
    </div>
  );
}
